import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageTitle, Btn, Toggle, Field, inputCls } from "./ui";
import { ImageField } from "./ImageField";

export default function ItemsAdmin({ name, title, sub, blank, fields, imageKey, imageLabel, preview }) {
  const [items, setItems] = useState(null);
  const snap = (x) => ({ ...x, _orig: x });
  const load = () => api.get(`/admin/${name}`).then(({ data }) => setItems(data.map(snap)));
  useEffect(() => { load(); }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (i, k, v) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v, _dirty: true } : x)));
  const body = (x) => Object.fromEntries(Object.keys(blank).map((k) => [k, x[k]]));

  const save = async (x) => {
    try {
      const { data } = x.id ? await api.put(`/admin/${name}/${x.id}`, body(x)) : await api.post(`/admin/${name}`, body(x));
      setItems((xs) => xs.map((y) => (y === x ? snap(data) : y)));
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (x) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      if (x.id) await api.delete(`/admin/${name}/${x.id}`);
      setItems((xs) => xs.filter((y) => y !== x));
      toast.success("Deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    const ordered = next.map((x, k) => ({ ...x, order: k, _orig: x._orig && { ...x._orig, order: k } }));
    setItems(ordered);
    try {
      await Promise.all(ordered.filter((x) => x.id).map((x) => api.put(`/admin/${name}/${x.id}`, body(x._orig))));
    } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const nextOrder = () => Math.max(-1, ...(items || []).map((x) => x.order ?? 0)) + 1;

  if (!items) return <p className="text-zinc-500">Loading…</p>;
  return (
    <div data-testid={`admin-${name}`}>
      <PageTitle title={title} sub={sub}>
        <Btn onClick={() => setItems([...items, { ...blank, order: nextOrder(), _dirty: true }])} data-testid={`add-${name}`}><Plus size={15} /> Add new</Btn>
      </PageTitle>
      {items.length === 0 && <p className="rounded-2xl border border-dashed border-[#D4AF37]/25 p-10 text-center text-sm text-zinc-500">No items yet.</p>}
      <div className="grid gap-5 lg:grid-cols-2">
        {items.map((x, i) => (
          <div key={x.id || `new-${i}`} className="rounded-2xl border border-[#D4AF37]/20 bg-[#101117] p-5" data-testid={`${name}-item-${i}`}>
            <div className="grid gap-5 sm:grid-cols-[160px_1fr]">
              <div>
                <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{imageLabel}</span>
                {x[imageKey] ? <ImageField value={x[imageKey]} onChange={(v) => patch(i, imageKey, v)} testid={`${name}-image-${i}`} />
                  : <div><div className="mb-2 aspect-[16/9] overflow-hidden rounded-xl border border-[#D4AF37]/25">{preview(x)}</div><ImageField value="" onChange={(v) => patch(i, imageKey, v)} testid={`${name}-image-${i}`} /></div>}
              </div>
              <div className="grid content-start gap-3 sm:grid-cols-2">
                {fields.map(([k, label]) => (
                  <Field key={k} label={label}><input value={x[k] ?? ""} onChange={(e) => patch(i, k, e.target.value)} className={inputCls} data-testid={`${name}-${k}-${i}`} /></Field>
                ))}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-white/5 pt-4">
              <Toggle checked={x.active} onChange={(v) => patch(i, "active", v)} testid={`${name}-active-${i}`} label={<span className="text-xs">{x.active ? "Visible" : "Hidden"}</span>} />
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="p-2 text-zinc-500 hover:text-white" aria-label="Move up" data-testid={`${name}-up-${i}`}><ArrowUp size={16} /></button>
                <button onClick={() => move(i, 1)} className="p-2 text-zinc-500 hover:text-white" aria-label="Move down" data-testid={`${name}-down-${i}`}><ArrowDown size={16} /></button>
                <button onClick={() => remove(x)} className="p-2 text-zinc-500 hover:text-red-400" aria-label="Delete" data-testid={`${name}-delete-${i}`}><Trash2 size={16} /></button>
                <Btn onClick={() => save(x)} disabled={!x._dirty} data-testid={`${name}-save-${i}`}><Save size={14} /> Save</Btn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
